//#region Imports Packages and Libs

import { Component, Vue } from "vue-facing-decorator";
import { store } from "../../store/store";
import { STORE_MUTATION } from "../../enums/store_mutation";
import { EVENTS } from "../../enums";
import { __vueEvent } from "../../constant";
import ERRORS from "./validation/error_messages";
import { isValidEmail, parseShareWith, ShareWithParseResult, validateNoteLength } from "./validation/validation";
import { TranscriptService } from "../../service/transcript_service";
import {
	SendTranscriptParams,
	TimeoutId,
	TranscriptApiResponse,
	TranscriptFormData,
	TranscriptMessageState,
	TranscriptStatus,
} from "../../types/components/transcript";
import { Util } from "../../util";
import { StringUtils } from "../../helpers/string_utils";
import { createLogger } from "./../../helpers/logger";
import { isMobile } from '../../helpers/is_mobile';
import { trapFocus } from "../../helpers/modals/focus_trap";
import { getDefaultSendTranscriptLabel, resolveSendTranscriptLabel } from "../../helpers/i18n/label_resolver";

//#endregion

//#region Define the component in class-style

@Component({})
export default class TranscriptModal extends Vue {
	//#region Class constants

	/**
	 * Time in milliseconds to display error messages before auto-hiding
	 */
	private readonly ERROR_MESSAGE_TIMEOUT_MS: number = 5000;

	/**
	 * Time in milliseconds to display success messages before auto-closing the modal
	 */
	private readonly SUCCESS_MESSAGE_TIMEOUT_MS: number = 3000;

	/**
	 * Maximum allowed character length for note field
	 */
	private readonly MAX_NOTE_LENGTH: number = 500;

	//#endregion

	//#region Class properties will be component data

	// Transcript service instance for API calls
	private transcriptService = new TranscriptService();

	// Logger instance for logging errors and messages
	private logger = createLogger({ context: "transcript_modal.ts" });

	// Track if user has started interacting with the form
	hasUserInteracted: boolean = false;

	// Track timeout for auto-hiding general error messages
	generalErrorTimeout: TimeoutId | null = null;

	// Track timeout for auto-closing after successful submission
	successMessageTimeout: TimeoutId | null = null;

	// Form data for the transcript modal
	transcriptForm: TranscriptFormData = {
		email: "",
		shareWith: "",
		note: "",
		isLoading: false,
		errors: {
			email: "",
			shareWith: "",
			note: "",
			general: "",
		},
	};

	// Success/error message states
	messageState: TranscriptMessageState = {
		successMessage: "",
		errorMessage: "",
	};
  
	// Cleanup function for focus trap
	cleanupTrap: (() => void) | null = null;

	//#endregion

	//#region Computed Properties

	/**
	 * Computed property to check if the form is valid for enabling/disabling the submit button
	 * @returns {boolean} - True if form has valid required fields
	 */
	get isFormValid(): boolean {
		try {
			const email = this.transcriptForm.email ? this.transcriptForm.email.trim() : "";
			const shareWith = this.transcriptForm.shareWith ? this.transcriptForm.shareWith.trim() : "";
			const note = this.transcriptForm.note ? this.transcriptForm.note.trim() : "";

			// Must have at least one recipient
			if (!email && !shareWith) return false;

			// Email must be valid if provided
			if (!this.validateEmail(email)) return false;

			// ShareWith must be valid if provided
			if (!this.validateShareWith(shareWith)) return false;

			// Note must be within limits if provided
			if (!this.validateNote(note)) return false;

			return true;
		} catch (error) {
			this.logError("isFormValid", error);
			return false;
		}
	}

	/**
	 * Show validation errors only after user interaction
	 */
	get shouldShowErrors(): boolean {
		return !!this.hasUserInteracted;
	}

	/**
	 * Gets whether the button is disabled.
	 *
	 * @returns {boolean} True if the button should be disabled, false otherwise.
	 *
	 * The button is disabled when:
	 * - The transcript form is loading
	 * - The form is not valid
	 * - The success message is not null or whitespace
	 */
	get isButtonDisabled(): boolean {
		return (
			this.transcriptForm.isLoading ||
			!this.isFormValid ||
			!StringUtils.isNullOrWhitespace(this.messageState.successMessage)
		);
	}

	/**
	 * Gets the text to display on the transcript button.
	 * @returns "Sending..." when the transcript form is loading, otherwise "Send Transcript"
	 */
	get buttonText(): string {
		return this.transcriptForm.isLoading 
			? this.getTranscriptLabel("send_button_sending")
			: this.getTranscriptLabel("send_button_label");
	}
	
	/**
	 * Gets the transcript modal element from the component's refs.
	 */
	get transcriptModal() {
		return this.$refs.transcript_modal as HTMLElement;
  }

	/**
	 * Build aria-describedby ids only when targets exist in DOM.
	 */
	get emailAriaDescribedBy(): string {
		return this.buildAriaDescribedBy(["emailRequirement"], this.transcriptForm.errors.email, "userEmailError");
	}

	get shareWithAriaDescribedBy(): string {
		return this.buildAriaDescribedBy(
			["emailRequirement", "shareWithHelpText"], this.transcriptForm.errors.shareWith, "shareWithError"
		);
	}

	get noteAriaDescribedBy(): string {
		return this.buildAriaDescribedBy(["noteHelpText"], this.transcriptForm.errors.note, "noteError");
	}

	/**
	 * Utility method to build aria-describedby attribute value
	 */
	private buildAriaDescribedBy(baseIds: string[], errorMessage: string, errorId: string): string {
		try {
			const ids = [...baseIds];
			if (!StringUtils.isNullOrWhitespace(errorMessage)) {
				ids.push(errorId);
			}
			return ids.join(" ");
		} catch {
			return baseIds.join(" ");
		}
	}

	//#endregion

	//#region Modal Lifecycle Hooks

	/**
	 * Vue lifecycle hook called when the component is mounted
	 * This is where we set up event listeners for showing/hiding the modal
	 */
	mounted(): void {
		this.listenEvents();
		this.initTrapFocus();
		this.focusFirstElement();
		this.refreshDomLabels(); // initialize labels on first render
	}

	/**
	 * Vue lifecycle hook called just before the component is destroyed
	 * This is where we clean up resources to prevent memory leaks
	 */
	beforeDestroy(): void {
		this.cleanupResources();
	}
	beforeUnmount(): void {
		this.cleanupTrapFocus();
	}

	/**
	 * Vue lifecycle hook called when the component is destroyed
	 * This is where we clean up resources
	 */
	destroyed(): void {
		this.cleanupResources();
	}

	/**
	 * Listen to Vue events for modal interactions
	 */
	listenEvents(): void {
		__vueEvent.on(EVENTS.ShowTranscriptModal, this.showModal);
		__vueEvent.on(EVENTS.HideTranscriptModal, this.closeModal);
		//__vueEvent.on(EVENTS.PageConfigUpdated, this.refreshDomLabels); // refresh labels on config change
	}

	//#endregion

	//#region Modal related logic here

	/**
	 * Shows the transcript modal and resets form state
	 */
	showModal(): void {
		try {
			this.resetForm();
			store.commit(STORE_MUTATION.SetShowTranscriptModal, true);
			this.refreshDomLabels(); // ensure labels reflect current pageConfig/language
		} catch (error) {
			this.logError("showModal", error);
		}
	}

	/**
	 * This method is called when the user clicks the close modal button or clicks outside modal.
	 */
	closeModal(): void {
		try {
			// Reset form state when closing
			this.resetForm();
			store.commit(STORE_MUTATION.SetShowTranscriptModal, false);
		} catch (error) {
			this.logError("closeModal", error);
		}
	}

	/**
	 * Reset all form data and error states
	 */
	resetForm(): void {
		try {
			// Clear all timeouts when resetting form
			this.clearGeneralErrorTimeout();
			this.clearSuccessMessageTimeout();

			// Reset form data to initial state
			// This ensures the form is ready for a new submission without stale data
			this.transcriptForm = {
				email: "",
				shareWith: "",
				note: "",
				isLoading: false,
				errors: {
					email: "",
					shareWith: "",
					note: "",
					general: "",
				},
			};

			this.messageState = {
				successMessage: "",
				errorMessage: "",
			};

			// Reset interaction tracking
			this.hasUserInteracted = false;
		} catch (error) {
			this.logError("resetForm", error);
		}
	}

	/**
	 * Focuses the first input element (user email field) in the modal after the next DOM update.
	 * Uses Vue's $nextTick to ensure the DOM has been updated before attempting to focus the element.
	 */
	focusFirstElement(): void {
		// Focus the first input element in the modal when it opens
		this.$nextTick(() => {
			const firstInput = this.$refs.userEmail as HTMLInputElement;

			// No need to focus if the chat modal is opened in mobile devices,
			// to prevent virtual keyboard issues on mobile & iOS devices
			if (firstInput && !isMobile.any()) {
				firstInput.focus({ preventScroll: true });
			}
		});
	}

	/**
	 * Mark that user has started interacting with form
	 */
	onUserInput(): void {
		this.hasUserInteracted = true;
		this.validateForm(); // Re-validate form on user input
	}
	
	/**
	 * This method is used to initialize the focus trap for the modal.
	 */
	initTrapFocus() {
		try {
			const modal = this.transcriptModal;
			if (!modal) return;
			this.cleanupTrap = trapFocus(modal);
		} catch {}
	}
	
	/**
	 * This method is used to clean up the focus trap when the modal is closed.
	 */
	cleanupTrapFocus() {
		try {
			if (this.cleanupTrap) this.cleanupTrap();
			this.cleanupTrap = null;
		} catch {}
	}
	
	//#endregion

	//#region Form Validation Logic

	/**
	 * Validates email field and sets appropriate error message
	 *
	 * @param {string} email - The email to validate
	 * @returns {boolean} - True if email is valid or empty, false otherwise
	 */
	private validateEmail(email: string): boolean {
		try {
			// Skip validation if email is empty (it's optional if shareWith is provided)
			if (!email) {
				return true;
			}

			// If email is invalid, set error message and return false
			if (!isValidEmail(email)) {
				this.transcriptForm.errors.email = this.getTranscriptLabel("email_invalid");
				return false;
			}

			// Email is valid
			return true;
		} catch (error) {
			this.logError("validateEmail", error);
			return false;
		}
	}

	/**
	 * Validates shareWith field and sets appropriate error message
	 *
	 * @param {string} shareWith - The shareWith input to validate
	 * @returns {boolean} - True if shareWith is valid or empty, false otherwise
	 */
	private validateShareWith(shareWith: string): boolean {
		try {
			// Skip validation if shareWith is empty (it's optional if email is provided)
			if (!shareWith) {
				return true;
			}

			// Parse and validate shareWith format
			const parseResult: ShareWithParseResult = parseShareWith(shareWith);

			// If invalid, set error message and return false
			if (!parseResult.valid) {
				// Resolve the i18n message HERE in the component
				const errorMessage = parseResult?.errorKey
					? this.getTranscriptLabel(parseResult?.errorKey)
					: parseResult.errorMessage || this.getTranscriptLabel("error_sharewith_invalid");

				this.transcriptForm.errors.shareWith = errorMessage;
				return false;
			}

			// ShareWith is valid
			return true;
		} catch (error) {
			this.logError("validateShareWith", error);
			return false;
		}
	}

	/**
	 * Validates note field and sets appropriate error message
	 *
	 * @param {string} note - The note text to validate
	 * @returns {boolean} - True if note is valid or empty, false otherwise
	 */
	private validateNote(note: string): boolean {
		try {
			// Skip validation if note is empty (it's optional)
			if (!note) {
				return true;
			}

			// If note is too long, set error message and return false
			if (!validateNoteLength(note)) {
				this.transcriptForm.errors.note = ERRORS.NOTE_TOO_LONG;
				return false;
			}

			// Note is valid
			return true;
		} catch (error) {
			this.logError("validateNote", error);
			return false;
		}
	}

	/**
	 * Initializes error state for a new validation cycle
	 *
	 * @returns {void}
	 */
	private initializeErrorState(): void {
		try {
			// Clear previous errors to start with a clean slate
			this.clearGeneralErrorTimeout();
			this.transcriptForm.errors = {
				email: "",
				shareWith: "",
				note: "",
				general: "",
			};
		} catch (error) {
			this.logError("initializeErrorState", error);
		}
	}

	/**
	 * Validates the entire transcript form and sets appropriate error messages.
	 *
	 * This method performs comprehensive validation of all form fields:
	 * - Ensures at least one recipient is specified (email or shareWith)
	 * - Validates email format if provided
	 * - Validates shareWith field format and content
	 * - Validates note length if provided
	 *
	 * @returns {boolean} True if the form is valid and ready for submission, false otherwise
	 */
	validateForm(): boolean {
		try {
			// Initialize error state for a new validation cycle
			this.initializeErrorState();

			// Don't show errors until user interaction
			if (!this.hasUserInteracted) {
				return this.isFormValid;
			}

			// Trim whitespace from all inputs to normalize user input
			const email = this.transcriptForm.email ? this.transcriptForm.email.trim() : "";
			const shareInput = this.transcriptForm.shareWith ? this.transcriptForm.shareWith.trim() : "";
			const note = this.transcriptForm.note ? this.transcriptForm.note.trim() : "";

			// Business Rule: At least one recipient must be specified
			if (!email && !shareInput) {
				this.setGeneralErrorWithTimeout(this.getTranscriptLabel("error_missing_recipient"));
			}

			// Validate each field using dedicated methods
			this.validateEmail(email);
			this.validateShareWith(shareInput);
			this.validateNote(note);

			// Check if any validation errors were found and return validation result
			const hasErrors = Object.values(this.transcriptForm.errors).some((msg) => Util.trimString(msg) !== "");
			return !hasErrors;
		} catch (error) {
			this.logError("validateForm", error);
			this.setGeneralErrorWithTimeout(this.getTranscriptLabel("error_unexpected"));
			return false;
		}
	}

	//#endregion

	//#region Form Submission Logic

	/**
	 * Handles exceptions during form submission
	 *
	 * @param {unknown} error - The caught exception
	 */
	private handleSubmitException(error: unknown): void {
		try {
			// Log the error for troubleshooting
			this.logError("handleSubmitException", error);

			// Set general error with auto-hide for submission failures
			this.setGeneralErrorWithTimeout(this.getTranscriptLabel("error_unexpected"));
			this.messageState.successMessage = "";
		} catch (innerError) {
			// Handle edge case where even error handling fails
			this.logError("handleSubmitException", innerError, "Error in exception handler");
		}
	}

	/**
	 * Prepares transcript data for API submission
	 *
	 * @returns {SendTranscriptParams} Formatted transcript data for API call
	 */
	private prepareTranscriptData(): SendTranscriptParams {
		try {
			// Trim whitespace from all inputs
			const email = this.transcriptForm.email ? this.transcriptForm.email.trim() : "";
			const shareWith = this.transcriptForm.shareWith ? this.transcriptForm.shareWith.trim() : "";
			const note = this.transcriptForm.note ? this.transcriptForm.note.trim() : "";

			// Parse comma-separated email addresses from shareWith field
			const parseResult: ShareWithParseResult = parseShareWith(shareWith);

			// Return the request parameters structured for the API
			return {
				primaryEmail: email,
				additionalEmails: parseResult.valid ? parseResult.emails : [],
				note,
			};
		} catch (error) {
			this.logError("prepareTranscriptData", error);
			// Return a minimal valid payload in case of error
			return {
				primaryEmail: this.transcriptForm.email ? this.transcriptForm.email.trim() : "",
				additionalEmails: [],
				note: "",
			};
		}
	}

	/**
	 * Handles a successful API response
	 *
	 * @param {TranscriptApiResponse} response - The successful API response
	 */
	private handleSuccessResponse(response: TranscriptApiResponse): void {
		try {
			// Set success message
			const isSuccessMessage = response.status_message?.includes("received");
			const successMessage = isSuccessMessage 
				? this.getTranscriptLabel("success_transcript_queued") 
				: response.status_message || "Transcript sent successfully!";
			this.messageState.successMessage = successMessage;
			this.messageState.errorMessage = "";

			// Show success message for 3 seconds, then close modal
			this.successMessageTimeout = setTimeout(() => {
				// Close the modal if it's still open and the success message is still showing
				if (this.messageState.successMessage) {
					this.closeModal();
				}
				this.successMessageTimeout = null;
			}, this.SUCCESS_MESSAGE_TIMEOUT_MS);
		} catch (error) {
			this.logError("handleSuccessResponse", error);
		}
	}

	/**
	 * Handles error response with i18n message resolution
	 * @param {TranscriptApiResponse} response - The error API response
	 */
	private handleErrorResponseWithI18n(response: TranscriptApiResponse): void {
		try {
			const statusMessage = response.status_message || "";
			// Check if status_message is an error key (starts with "error_")
			const isErrorKey = response.status_message?.startsWith("error_");
			
			// Check if it's a rate limit message (starts with "Please wait")
      const isRateLimitMessage = statusMessage.toLowerCase().startsWith("please wait");
			let errorMessage: string;
			
			if (isErrorKey) {
				// Resolve i18n key
				errorMessage = this.getTranscriptLabel(statusMessage);
			} else if (isRateLimitMessage) {
				// Use rate limit message
				errorMessage = this.getTranscriptLabel("wait_before_resending");
			} else {
				// Use message as-is or fallback to default error
				errorMessage = statusMessage || this.getTranscriptLabel("error_send_failed");
			}

			this.setGeneralErrorWithTimeout(errorMessage);
			this.messageState.successMessage = "";
		} catch (error) {
			this.logError("handleErrorResponseWithI18n", error);
			this.setGeneralErrorWithTimeout(this.getTranscriptLabel("error_send_failed"));
		}
	}

	/**
	 * Handles form submission for sending transcript
	 * @param {Event} event - Form submission event
	 */
	async submitTranscript(event: Event) {
		try {
			event.preventDefault();

			// Validate form before submission
			if (!this.validateForm()) {
				return;
			}

			this.transcriptForm.isLoading = true;

			// Prepare request payload for the transcript service
			const requestData = this.prepareTranscriptData();

			// Call the actual transcript service
			const response = await this.transcriptService.sendTranscript(requestData);

			// Handle the API response based on status
			if (StringUtils.normalizeString(response.status) === String(TranscriptStatus.Success)) {
				this.handleSuccessResponse(response);
			} else {
				this.handleErrorResponseWithI18n(response);
			}
		} catch (error) {
			this.handleSubmitException(error);
		} finally {
			this.transcriptForm.isLoading = false;
		}
	}	

	//#endregion
	
	//#region Refresh DOM Labels
	
	/**
	 * Retrieve a localized label specific to the transcript modal by key, using default labels as fallback.
	 */
	private getTranscriptLabel(key: string): string {
		try {
			const pageConfig = this.$store.state.pageConfig;
			return resolveSendTranscriptLabel(key, pageConfig);
		} catch (ex) {
			return getDefaultSendTranscriptLabel(key);
		}
	}
	
	/**
	 * Safely assigns the provided text to the element's textContent if the element appears to be an HTMLElement.
	 * @param element - The target element (of unknown type). 
	 * @param text - The text to set on the element's textContent.
	 */
	private setElementText(element: unknown, text: string): void {
		try {
			if (element && typeof (element as HTMLElement).textContent === 'string') {
				(element as HTMLElement).textContent = text;
			}
		} catch {}
	}
	
	/**
	 * Sets a single attribute on the given element if it supports setAttribute.
	 */
	private setElementAttribute(element: unknown, attrName: string, attrValue: string): void {
		try {
			if (element && (element as HTMLElement).setAttribute) {
				(element as HTMLElement).setAttribute(attrName, attrValue);
			}
		} catch {}
	}
	
	/**
	 * Sets multiple attributes on a target element if it implements setAttribute. 
	 * @param element - The target to receive attributes. 
	 * @param attributes - A record mapping attribute names to string values. 
	 */
	private setElementAttributes(element: unknown, attributes: Record<string, string>): void {
		try {
			if (!element || !(element as HTMLElement).setAttribute) return;
			for (const [attrName, attrValue] of Object.entries(attributes)) {
				(element as HTMLElement).setAttribute(attrName, attrValue);
			}
		} catch {}
	}

	/**
	 * This method refreshes text content, placeholders, titles, and aria attributes
	 * for various UI elements in the transcript modal based on configured labels.
	 */
	private refreshDomLabels(): void {
    try {
      this.$nextTick(() => {
        // Close button (title + aria-label)
        const closeText = this.getTranscriptLabel("close_modal");
        this.setElementAttributes(this.$refs.closeButton, { title: closeText, "aria-label": closeText });

        // Heading paragraph (first <p> near the top of the form)
        this.setElementText(this.$refs.formHeadingLine1, this.getTranscriptLabel("form_heading_line1"));
        this.setElementText(this.$refs.formHeadingLine2, this.getTranscriptLabel("form_heading_line2"));
        this.setElementText(this.$refs.emailRequirement, this.getTranscriptLabel("error_missing_recipient"));
        
        // Inputs: userEmail label, placeholder, and aria-label
        const emailText = this.getTranscriptLabel("email_placeholder");
        this.setElementText(this.$refs.userEmailLabel, emailText);
        this.setElementAttributes(this.$refs.userEmail, { placeholder: emailText });

        // Inputs: shareWith label, placeholder, and aria-label
        const shareWithText = this.getTranscriptLabel("sharewith_placeholder");
        this.setElementText(this.$refs.shareWithLabel, shareWithText);
        this.setElementAttributes(this.$refs.shareWith, { placeholder: shareWithText });
        this.setElementText(this.$refs.shareWithHelpText, this.getTranscriptLabel("sharewith_help"));

        // Inputs: note label, placeholder, and aria-label
        const noteText = this.getTranscriptLabel("note_placeholder");
        this.setElementText(this.$refs.noteLabel, noteText);
        this.setElementAttributes(this.$refs.note, { placeholder: noteText });
        this.setElementText(this.$refs.noteHelpText, this.getTranscriptLabel("note_help"));
      });
    } catch (error) {
      this.logError("refreshDomLabels", error);
    }
  }
	
	//#endregion

	//#region Utility Methods

	/**
	 * Logs errors in a standardized format across the component
	 *
	 * @param {string} methodName - Name of the method where error occurred
	 * @param {unknown} error - The error that occurred
	 * @param {string} [context] - Optional additional context about the error
	 */
	logError(methodName: string, error: unknown, context?: string): void {
		const contextInfo = context ? ` - ${context}` : "";
		this.logger.error(`Error in ${methodName}${contextInfo}:`, error);
	}

	/**
	 * Gets the character count for the note field
	 * @returns {number} - Current character count
	 */
	getNoteCharacterCount(): number {
		return this.transcriptForm.note ? this.transcriptForm.note.length : 0;
	}

	/**
	 * Gets remaining character count for the note field
	 * @returns {number} - Remaining characters allowed
	 */
	getRemainingCharacterCount(): number {
		return this.MAX_NOTE_LENGTH - this.getNoteCharacterCount();
	}

	/**
	 * Sets general error message and schedules auto-hide after a specified timeout
	 * Creates a timeout that will be automatically cleared when the component is destroyed
	 *
	 * @param {string} message - Error message to display
	 */
	setGeneralErrorWithTimeout(message: string): void {
		try {
			// Clear any existing timeout first to prevent memory leaks
			this.clearGeneralErrorTimeout();

			// Early return for empty or invalid messages
			if (!message || typeof message !== "string" || message.trim() === "") {
				return;
			}

			this.transcriptForm.errors.general = message;
			this.generalErrorTimeout = setTimeout(() => {
				this.transcriptForm.errors.general = "";
				this.generalErrorTimeout = null;
			}, this.ERROR_MESSAGE_TIMEOUT_MS);
		} catch (error) {
			this.logError("setGeneralErrorWithTimeout", error);
		}
	}

	/**
	 * Clears the general error timeout if it exists
	 */
	clearGeneralErrorTimeout(): void {
		try {
			if (this.generalErrorTimeout) {
				clearTimeout(this.generalErrorTimeout);
				this.generalErrorTimeout = null;
			}
		} catch (error) {
			this.logError("clearGeneralErrorTimeout", error);
		}
	}

	/**
	 * Clears the success message timeout if it exists
	 */
	clearSuccessMessageTimeout(): void {
		try {
			if (this.successMessageTimeout) {
				clearTimeout(this.successMessageTimeout);
				this.successMessageTimeout = null;
			}
		} catch (error) {
			this.logError("clearSuccessMessageTimeout", error);
		}
	}

	/**
	 * Cleans up all resources used by the component
	 * Ensures all timeouts are cleared to prevent memory leaks
	 */
	cleanupResources(): void {
		try {
			// Clear all timeouts
			this.clearGeneralErrorTimeout();
			this.clearSuccessMessageTimeout();

			// Remove event listeners
			__vueEvent.off(EVENTS.ShowTranscriptModal, this.showModal);
			__vueEvent.off(EVENTS.HideTranscriptModal, this.closeModal);
		} catch (error) {
			this.logError("cleanupResources", error);
		}
	}

	//#endregion
}

//#endregion
